// Implements: F-MCK-002, F-MCK-011, F-UIX-007
/**
 * The gallery's toolbar (the mockup's `.toolbar`): the tabs on the left; on the right, while a list
 * of mockups is shown, the search, the scope and tag filters and the grid/list switch. The trash tab
 * hides the right side: nothing there searches or filters. Pure presentation, every change goes up.
 *
 * Mockup v3 P8: the order menu ("Más recientes", "Actualizados", "Nombre A-Z", the mockup's `SORT_L`)
 * sits after the filters; its button names the current order. Each tab carries its count once the
 * server has given them (`counts`), and none while they are unknown.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { buttonClassName } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { Menu } from "../../../shared/ui/molecules/Menu";
import type { MenuEntry, MenuTriggerProps } from "../../../shared/ui/molecules/Menu";
import { SearchField } from "../../../shared/ui/molecules/SearchField";
import { SegmentedControl } from "../../../shared/ui/molecules/SegmentedControl";
import { Tabs } from "../../../shared/ui/molecules/Tabs";
import type { GalleryCountsView, GallerySort } from "../../../types/api";
import styles from "./GalleryToolbar.module.css";

export type GalleryTab = "all" | "favorites" | "trash";
export type GalleryMode = "grid" | "list";

/** The orders the listing serves, in the menu's order. */
export const GALLERY_SORTS: readonly GallerySort[] = ["recent", "updated", "name"];

export interface FilterOption {
  readonly value: string;
  readonly label: string;
}

export interface GalleryToolbarProps {
  /** Prefix of the tab and panel ids. */
  readonly idBase: string;
  readonly tab: GalleryTab;
  readonly onTab: (tab: GalleryTab) => void;
  readonly search: string;
  readonly onSearch: (value: string) => void;
  /** The scope menu: `selected` is one of the options' values; its label is shown on the button. */
  readonly scope: {
    readonly options: readonly FilterOption[];
    readonly selected: string;
    readonly onSelect: (value: string) => void;
  };
  /** The tag menu; absent while no tag is known. `selected` is `""` for every tag. */
  readonly tag: {
    readonly options: readonly FilterOption[];
    readonly selected: string;
    readonly onSelect: (value: string) => void;
  } | null;
  /** Only the tabs: an empty gallery has nothing to search or filter, but its trash may hold mockups. */
  readonly tabsOnly?: boolean | undefined;
  readonly mode: GalleryMode;
  readonly onMode: (mode: GalleryMode) => void;
  /** The order of the list (mockup v3 P8). */
  readonly sort: GallerySort;
  readonly onSort: (sort: GallerySort) => void;
  /** The size of each tab; `null` while unknown or unreadable (the tabs then carry no number). */
  readonly counts?: GalleryCountsView | null | undefined;
}

function filterMenu(
  label: string,
  buttonLabel: string,
  options: readonly FilterOption[],
  selected: string,
  onSelect: (value: string) => void,
  icon: "filter" | "list" = "filter",
): ReactElement {
  const entries: readonly MenuEntry[] = options.map((option) => ({
    kind: "radio",
    id: `option-${option.value}`,
    label: option.label,
    checked: option.value === selected,
    onSelect: () => {
      onSelect(option.value);
    },
  }));
  return (
    <Menu
      label={label}
      entries={entries}
      renderTrigger={(trigger: MenuTriggerProps) => (
        <button type="button" {...trigger} className={buttonClassName("secondary")}>
          <Icon name={icon} size="sm" />
          {buttonLabel}
          <Icon name="chevron-down" size="sm" />
        </button>
      )}
    />
  );
}

export function GalleryToolbar({
  idBase,
  tab,
  onTab,
  search,
  onSearch,
  scope,
  tag,
  tabsOnly = false,
  mode,
  onMode,
  sort,
  onSort,
  counts = null,
}: GalleryToolbarProps): ReactElement {
  const { t } = useTranslation();
  const scopeName = scope.options.find((option) => option.value === scope.selected)?.label ?? t("gallery.scope.all");
  const tagName = tag?.options.find((option) => option.value === tag.selected)?.label ?? t("gallery.tag.all");
  const sortOptions: readonly FilterOption[] = GALLERY_SORTS.map((value) => ({ value, label: t(`gallery.sort.${value}`) }));

  return (
    <div className={styles.toolbar}>
      <Tabs
        label={t("gallery.tabs.label")}
        idBase={idBase}
        items={[
          { id: "all", label: t("gallery.tab.all"), count: counts?.all },
          { id: "favorites", label: t("gallery.tab.favorites"), count: counts?.favorites },
          { id: "trash", label: t("gallery.tab.trash"), icon: "trash", count: counts?.trash },
        ]}
        selected={tab}
        onSelect={(id) => {
          onTab(id === "favorites" ? "favorites" : id === "trash" ? "trash" : "all");
        }}
      />
      {tab === "trash" || tabsOnly ? null : (
        <>
          <span className={styles.spacer} />
          <SearchField
            label={t("gallery.search.label")}
            placeholder={t("gallery.search.placeholder")}
            value={search}
            onChange={onSearch}
          />
          {filterMenu(
            t("gallery.scope.menu"),
            t("gallery.scope.button", { name: scopeName }),
            scope.options,
            scope.selected,
            scope.onSelect,
          )}
          {tag === null
            ? null
            : filterMenu(
                t("gallery.tag.menu"),
                t("gallery.tag.button", { name: tagName }),
                tag.options,
                tag.selected,
                tag.onSelect,
              )}
          {filterMenu(
            t("gallery.sort.label"),
            t("gallery.sort.button", { name: t(`gallery.sort.${sort}`) }),
            sortOptions,
            sort,
            (value) => {
              onSort(GALLERY_SORTS.find((each) => each === value) ?? "recent");
            },
            "list",
          )}
          <SegmentedControl<GalleryMode>
            label={t("gallery.view.label")}
            iconOnly
            options={[
              { value: "grid", label: t("gallery.view.grid"), icon: "grid" },
              { value: "list", label: t("gallery.view.list"), icon: "list" },
            ]}
            value={mode}
            onChange={onMode}
          />
        </>
      )}
    </div>
  );
}
